import { computed, nextTick } from "vue";
import { useForm } from "@tanstack/vue-form";
import { useDateUtils } from "../../../../room-booking-management/calendar/composables/useDateUtils";

/**
 * Lean form composable for course appointments.
 *
 * Mirrors the room booking form shape (so the shared leaf components work
 * unchanged) but drops everything booking-specific: person association,
 * student budget/transaction and mail notifications.
 *
 * Modes:
 *  - create        => create-instance / create-series
 *  - edit           => update-instance (standalone appointment)
 *  - edit-instance  => update-instance (occurrence detaches from its series)
 *  - edit-series    => update-series
 */
export function useCourseAppointmentForm(props, emit) {
	const { formatDateForInput, formatTimeForInput } = useDateUtils();

	const getCleanDefaultValues = () => ({
		startDate: "",
		startTime: "",
		endDate: "",
		endTime: "",
		title: "",
		description: "",
		employeeId: null,
		employeeName: "",
		rrulePattern: "",
		cancelledDates: [],
		roomId: null,
		bookingTypeId: null,
		ignoreOpeningHours: false,
	});

	const form = useForm({
		defaultValues: getCleanDefaultValues(),
		onSubmit: async ({ value }) => {
			emitSubmit(buildPayloadFromForm(value));
		},
	});

	const modalTitle = computed(
		() =>
			({
				create: "Termin planen",
				edit: "Termin bearbeiten",
				"edit-instance": "Einzeltermin bearbeiten",
				"edit-series": "Terminreihe bearbeiten",
			}[props.mode] || "Termin")
	);

	const isSeriesMode = computed(
		() => props.mode === "create" || props.mode === "edit-series"
	);

	function buildPayloadFromForm(values) {
		// A series anchor always spans a single day: the server re-expands every occurrence from the
		// anchor's date and reuses only the end's time of day. Pinning the end to the start date keeps
		// the anchor valid when the start date is moved.
		const endDate = props.mode === "edit-series" ? values.startDate : values.endDate;

		const finalStartTime = new Date(`${values.startDate}T${values.startTime}`);
		const finalEndTime = new Date(`${endDate}T${values.endTime}`);

		const data = {
			title:
				values.title && values.title.trim() !== ""
					? values.title.trim()
					: null,
			description:
				values.description && values.description.trim() !== ""
					? values.description.trim()
					: null,
			startTime: finalStartTime.toISOString(),
			endTime: finalEndTime.toISOString(),
			roomId: values.roomId || null,
			bookingTypeId: values.bookingTypeId || null,
			employeeId: values.employeeId || null,
		};

		if (
			props.mode === "edit-series" ||
			(values.rrulePattern && values.rrulePattern.trim())
		) {
			data.pattern = values.rrulePattern;
			data.cancellations = values.cancelledDates || [];
		}

		return data;
	}

	function emitSubmit(data) {
		if (props.mode === "create") {
			const isSeries = data.pattern && data.pattern.trim();
			emit("submit", {
				action: isSeries ? "create-series" : "create-instance",
				method: "POST",
				data,
			});
		} else if (props.mode === "edit" || props.mode === "edit-instance") {
			emit("submit", {
				action: "update-instance",
				method: "PUT",
				data: { ...data, id: props.appointment?.id },
			});
		} else if (props.mode === "edit-series") {
			emit("submit", {
				action: "update-series",
				method: "PUT",
				data: { ...data, id: props.series?.id },
			});
		}
	}

	function populateForm() {
		nextTick(() => {
			if (props.mode === "edit-series" && props.series) {
				populateFrom(props.series, props.series.pattern, props.series.cancellations);
			} else if (
				(props.mode === "edit" || props.mode === "edit-instance") &&
				props.appointment
			) {
				populateFrom(props.appointment, "", []);
			} else {
				// Create mode: default the date range to the semester's lecture
				// period (Vorlesungszeit). The user can adjust it for single dates.
				// Title and employee ("Fremdbuchung") are pre-filled but editable.
				form.reset({
					...getCleanDefaultValues(),
					startDate: props.lecturePeriodStart || "",
					endDate: props.lecturePeriodEnd || props.lecturePeriodStart || "",
					title: props.defaultTitle || "",
					employeeId: props.defaultEmployeeId || null,
					employeeName: props.defaultEmployeeName || "",
				});
			}
		});
	}

	function populateFrom(entity, pattern, cancellations) {
		const start = new Date(entity.startTime);
		const end = new Date(entity.endTime);

		form.reset({
			...getCleanDefaultValues(),
			startDate: formatDateForInput(start),
			startTime: formatTimeForInput(start),
			endDate: formatDateForInput(end),
			endTime: formatTimeForInput(end),
			title: entity.title ?? "",
			description: entity.description ?? "",
			employeeId: entity.employeeId || null,
			employeeName: entity.employeeName || "",
			roomId: entity.roomId || null,
			bookingTypeId: entity.bookingTypeId || null,
			rrulePattern: pattern || "",
			cancelledDates: cancellations || [],
		});
	}

	return {
		form,
		modalTitle,
		isSeriesMode,
		populateForm,
	};
}
